import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import { useGetAccThemeDetailsMutation } from "../api-integration/secure/secure";
import { useAppSelector } from "../hooks/redux";
import { selectUser } from "../api-integration/common.slice";
import { hexToRgb } from "../util/util";
import { isKioskHostname } from "../kiosk/util/kiosk";

export interface Theme {
    themeId: number;
    themeName: string;
    companyBackgroundImageURL?: string;
    logoURL?: string;
    aboutURL: string;
    contactURL: string;
    termsURL: string;
    privacyURL: string;
    textColor: string;
    buttonColor: string;
    iconColor: string;
    navBarBgColor: string;
    navBarTextColor: string;
    hrefTextColor: string;

}

interface ThemeContextType {
    theme: Theme;
    setTheme: React.Dispatch<React.SetStateAction<Theme>>;
    applyThemeToCSS: (t: Theme) => void 
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
    return ctx;
};

interface ThemeProviderProps {
    children: React.ReactNode;
}

const defaultTheme: Theme = {
    themeId: 0,
    themeName: "",
    companyBackgroundImageURL: "",
    logoURL: "",
    aboutURL: "",
    contactURL: "",
    termsURL: "",
    privacyURL: "",
    textColor: "#007dbb",
    buttonColor: "#00CCFF",
    iconColor: "#007dbb",
    navBarBgColor: "",
    navBarTextColor: "#007dbb",
    hrefTextColor: "#00CCFF"
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
    const user = useAppSelector(selectUser);
    const hostname = window.location.hostname.toLowerCase();
    const [theme, setTheme] = useState<Theme>(defaultTheme);

    const [getAccThemeAPI, {data: getThemeDetails, isSuccess: isGetThemeDetailsSuccess, isError: isGetThemeDetailsError, error: getThemeDetailsError}] 
    = useGetAccThemeDetailsMutation();

    const applyThemeToCSS = useCallback((t: Theme) => {
        const root = document.documentElement;

        if (t.companyBackgroundImageURL) {
            root.style.setProperty("--bg-url", `url(${t.companyBackgroundImageURL})`);
        }
        const textColor = t.textColor || defaultTheme.textColor;
        root.style.setProperty("--secondary-color", textColor);
        root.style.setProperty("--bs-body-color", textColor);
        root.style.setProperty("--bs-heading-color", textColor);
        {
            const { r, g, b } = hexToRgb(textColor);
            root.style.setProperty("--bs-body-color-rgb", `${r}, ${g}, ${b}`);
        }

        const buttonColor = t.buttonColor || defaultTheme.buttonColor;
        root.style.setProperty("--button-color", buttonColor);
        {
            const { r, g, b } = hexToRgb(buttonColor);
            root.style.setProperty("--button-color-rgb", `${r}, ${g}, ${b}`);
        }

        const iconColor = t.iconColor || defaultTheme.iconColor;
        root.style.setProperty("--icon-color", iconColor);
        {
            const { r, g, b } = hexToRgb(iconColor);
            root.style.setProperty("--icon-color-rgb", `${r}, ${g}, ${b}`);
        }

        const navBarBgColor = t.navBarBgColor || defaultTheme.navBarBgColor;
        root.style.setProperty("--navbar-bg-color", navBarBgColor);
        {
            const { r, g, b } = hexToRgb(navBarBgColor);
            root.style.setProperty("--navbar-bg-color-rgb", `${r}, ${g}, ${b}`);
        }

        const navBarTextColor = t.navBarTextColor || defaultTheme.navBarTextColor;
        root.style.setProperty("--navbar-text-color", navBarTextColor);
        {
            const { r, g, b } = hexToRgb(navBarTextColor);
            root.style.setProperty("--navbar-text-color-rgb", `${r}, ${g}, ${b}`);
        }

        const hrefTextColor = t.hrefTextColor || defaultTheme.hrefTextColor;
        root.style.setProperty("--href-text-color", hrefTextColor);
        {
            const { r, g, b } = hexToRgb(hrefTextColor);
            root.style.setProperty("--href-text-color-rgb", `${r}, ${g}, ${b}`);
        }
    }, []);

    useEffect(() => {
        if(isGetThemeDetailsSuccess) {
        if(getThemeDetails?.success === true) {
            let orgTheme = {} as Theme;
            if(getThemeDetails?.themeDetails) {
            orgTheme = getThemeDetails?.themeDetails || {};
            }
            else {
            orgTheme = theme;
            }
            setTheme(orgTheme);
            applyThemeToCSS(orgTheme);
        }
        else {

        }
        }
        else if(isGetThemeDetailsError || getThemeDetailsError) {

        }
    },[isGetThemeDetailsSuccess, isGetThemeDetailsError, getThemeDetailsError, getThemeDetails]);

    useEffect(() => {
        applyThemeToCSS(theme);
    }, [theme]);

    useEffect(() => {
        // loadTheme();
        if(!isKioskHostname(hostname) && user?.userId !== undefined && user?.userId !== null && user?.userId > 0) {
            getAccThemeAPI({});
        }
    }, [user?.userId]);

    const contextValue = useMemo(() => ({ theme, setTheme, applyThemeToCSS }), [theme, applyThemeToCSS]);

    return (
        <ThemeContext.Provider value={contextValue}>
        {children}
        </ThemeContext.Provider>
    );
};
